import React from "react";
import {
  AspectRatio,
  Box,
  Skeleton,
  useColorModeValue,
} from "@chakra-ui/react";
import { HomePageCourseProgress } from "./HomePageCourseProgress";
import { HomePageExamProgress } from "./HomePageExamProgress";
import { ReactComponent as Rocket } from "../../assets/rocket.svg";
import { graphql, useStaticQuery } from "gatsby";
import { isAttemptExpired, useUser } from "../../utils";

export function HomePageHeroImage() {
  const { tutorials, exams } =
    useStaticQuery<Queries.HomePageHeroImageQuery>(graphql`
      query HomePageHeroImage {
        tutorials: allCourse {
          nodes {
            slug
            progressId
          }
        }
        exams: allExam {
          nodes {
            slug
            progressId
          }
        }
      }
    `);

  const { user, loading } = useUser();
  const bg = useColorModeValue("blue.800", "black.100");

  if (user?.courses?.length) {
    const userAttempts = user.attempts ?? [];
    const [inProgressAttempt] = userAttempts
      .filter(
        (attempt) =>
          !attempt.completedAt && !isAttemptExpired(new Date(attempt.startedAt))
      )
      .sort(
        (a, b) =>
          new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime()
      );

    // first, check to see if there's an in-progress exam and render that
    if (inProgressAttempt) {
      const examIds = exams.nodes.map((node) => node.progressId);
      if (examIds.includes(inProgressAttempt.testId)) {
        return <HomePageExamProgress attempt={inProgressAttempt} />;
      }
    }

    const userCourses = user.courses ?? [];
    const [inProgressCourse] = userCourses
      .filter((course) => !course.completedAt)
      .sort(
        (a, b) =>
          new Date(b.enrolledAt).getTime() - new Date(a.enrolledAt).getTime()
      );

    // otherwise, check to see if there's an in-progress course and render that
    if (inProgressCourse) {
      const tutorialIds = tutorials.nodes.map((node) => node.progressId);
      if (tutorialIds.includes(inProgressCourse.id)) {
        const completedTaskIds = user.tasks?.map((task) => task.id) ?? [];
        return (
          <HomePageCourseProgress
            completedTaskIds={completedTaskIds}
            courseId={inProgressCourse.id}
          />
        );
      }
    }
  }

  return (
    <AspectRatio bg={bg} ratio={16 / 9}>
      {loading ? (
        <Skeleton />
      ) : (
        /*
          magic percent is the exact proportions of the space between rocket and
          its container in figma
          https://www.figma.com/file/BgvwTGkv50ZWUXjlmC1IJB/Odyssey-%E2%80%93-Homepage?node-id=1667%3A19299
        */
        <Box as={Rocket} pr={`${(61.09 / 720) * 100}%`} fill="beige.300" />
      )}
    </AspectRatio>
  );
}
